import { Search, X } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { BulkBar, toggleAll, toggleId } from '../components/BulkBar';
import { ExportMenu } from '../components/ExportMenu';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { exportPosts } from '../lib/api/export';
import { useBulkDeletePosts, useCategories, usePosts } from '../lib/api/posts';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';
import { fmtDateTime, fmtRelative } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import { plainText, truncate } from '../lib/utils';

export default function Posts(): JSX.Element {
  return <PostsList type="post" title="Posts" />;
}

interface ListProps {
  type: 'post' | 'page';
  title: string;
}

export function PostsList({ type, title }: ListProps): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [sort, setSort] = useState<'newest' | 'oldest' | 'relevance'>('newest');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const deferredQ = useDeferredValue(q);

  const bulkDelete = useBulkDeletePosts();
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();

  const cats = useCategories(locale);
  const list = usePosts({
    locale,
    type,
    q: deferredQ.trim() || undefined,
    category: category === 'all' ? undefined : category,
    sort: deferredQ.trim() ? sort : sort === 'relevance' ? 'newest' : sort,
    page,
    pageSize,
  });

  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageIds = (list.data?.items ?? []).map((p) => p.id);

  return (
    <>
      <Topbar title={title} subtitle={`Locale: ${locale.toUpperCase()}`} />
      <PageHeader
        title={title}
        subtitle={`${total.toLocaleString()} ${type}s in ${locale.toUpperCase()}`}
        actions={
          <ExportMenu
            options={[
              {
                format: 'csv',
                label: 'CSV (current filters)',
                description: 'Spreadsheet — applies the locale, search and category above',
                run: () =>
                  exportPosts({
                    format: 'csv',
                    type,
                    locale,
                    q: deferredQ.trim() || undefined,
                    category: category === 'all' ? undefined : category,
                    sort: sort === 'relevance' ? 'newest' : sort,
                  }),
              },
              {
                format: 'json',
                label: 'JSON (current filters)',
                description: 'Full content + metadata for backup or migration',
                run: () =>
                  exportPosts({
                    format: 'json',
                    type,
                    locale,
                    q: deferredQ.trim() || undefined,
                    category: category === 'all' ? undefined : category,
                    sort: sort === 'relevance' ? 'newest' : sort,
                  }),
              },
            ]}
          />
        }
      />
      <div className="space-y-4 px-6 pb-10">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface p-3 shadow-card">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <Input
              placeholder="Search title and content…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              className="pl-8"
            />
            {q ? (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-subtle hover:text-ink"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>

          <Select
            value={category}
            onValueChange={(v) => {
              setCategory(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-10 w-[200px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.data?.items.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>
                  {c.name} ({c.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={sort}
            onValueChange={(v) => {
              setSort(v as typeof sort);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-10 w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="relevance" disabled={!deferredQ.trim()}>
                Relevance
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {list.isLoading ? (
          <div className="rounded-lg border border-line bg-surface p-10">
            <Spinner label="Loading…" />
          </div>
        ) : Array.isArray(list.data?.items) && list.data.items.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
            <table className="table-base">
              <thead>
                <tr>
                  <th className="w-8">
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={pageIds.length > 0 && pageIds.every((id) => selected.has(id))}
                      onChange={() => setSelected(toggleAll(selected, pageIds))}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="w-16">Image</th>
                  <th>Title</th>
                  <th className="w-48">Categories</th>
                  <th className="w-44">Author</th>
                  <th className="w-44">Published</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((p) => (
                  <tr key={p.id} className={selected.has(p.id) ? 'bg-brand/5' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        checked={selected.has(p.id)}
                        onChange={() => setSelected(toggleId(selected, p.id))}
                        aria-label={`Select ${p.slug}`}
                      />
                    </td>
                    <td>
                      {p.featuredImage?.url ? (
                        <img
                          src={p.featuredImage.url}
                          alt={p.featuredImage.alt ?? ''}
                          className="h-10 w-14 rounded object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-10 w-14 rounded bg-surface-muted" />
                      )}
                    </td>
                    <td>
                      <Link
                        to={`/${type}s/${p.slug}`}
                        className="font-medium text-ink hover:text-brand"
                      >
                        {truncate(plainText(p.title), 100)}
                      </Link>
                      <p className="text-xs text-ink-subtle">
                        {truncate(plainText(p.excerpt), 120)}
                      </p>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(p.categories ?? []).slice(0, 3).map((c) => (
                          <Badge key={c.slug} variant="outline">
                            {c.name}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="text-sm text-ink-muted">{p.author?.name ?? '—'}</td>
                    <td className="text-sm text-ink-muted" title={fmtDateTime(p.publishedAt)}>
                      {fmtRelative(p.publishedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm text-ink-muted">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Empty title={`No ${type}s found`} description="Try a different filter or search term." />
        )}
      </div>
      <BulkBar
        selected={selected}
        onClear={() => setSelected(new Set())}
        entityLabel={type}
        deleting={bulkDelete.isPending}
        onDelete={async () => {
          if (
            !(await confirm({
              title: `Permanently delete ${selected.size} ${type}(s)?`,
              description: 'This cannot be undone.',
              confirmLabel: 'Delete',
            }))
          )
            return;
          try {
            const result = await bulkDelete.mutateAsync([...selected]);
            showToast({ title: `${result.deleted} ${type}(s) deleted`, variant: 'success' });
            setSelected(new Set());
          } catch (err) {
            showToast({ title: 'Delete failed', description: (err as Error).message, variant: 'danger' });
          }
        }}
      />
      {confirmDialog}
    </>
  );
}
